import { IconButton, Tooltip } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { useKeyCommand } from '@/hooks/useKeyCommand';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { useCurrent } from '@/hooks/useCurrent';
import { useGlobalSearchContext } from '@/imports/ui/components/search/SearchProvider';

import { VersionFilePreviewFromQuery } from './components/files/VersionFilePreviewFromQuery';
import { AIAskDrawer } from './components/drawer/Drawer';
import { AIAskTriggerIcon } from './components/drawer/Icons';
import { useAskQuestion } from './hooks/useAskQuestion';
import { useOpenResultsPage } from './hooks/useOpenResultsPage';
import { useSavedChat } from './hooks/useSavedChat';

function pathWithoutVersionFileId(asPath: string): string {
  const [pathname, search = ''] = asPath.split('?');
  if (!search) return pathname;
  const params = new URLSearchParams(search);
  params.delete('versionFileId');
  const next = params.toString();
  return next ? `${pathname}?${next}` : pathname;
}

export const AIAsk: React.FC<{
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}> = ({ isOpen, onOpen, onClose }) => {
  const searchClient = useGlobalSearchContext();
  const memberManagement = useMemberManagement();
  const current = useCurrent();
  const router = useRouter();

  const [askQuestion, setAskQuestion] = React.useState('');
  const [askLoading, setAskLoading] = React.useState(false);
  const askInputRef = React.useRef<HTMLInputElement>(null);

  const userId = current.user?._id ?? '';
  const tenantId = current.tenant?._id ?? '';

  const {
    askMessages,
    setAskMessages,
    askSessionId,
    setAskSessionId,
    askError,
    setAskError,
    clearPersistedChat,
  } = useSavedChat(userId, tenantId);

  const { ask } = useAskQuestion({
    aiSearchHttp: searchClient.aiSearchHttp,
    userId,
    askSessionId,
    setAskSessionId,
    setAskMessages,
    setAskError,
    setAskLoading,
  });

  const indexData = searchClient.indexData;
  const { viewResultsLoadingIndex, handleViewResults } = useOpenResultsPage({
    askMessages,
    setAskMessages,
    setAskError,
  });

  const appOrigin =
    typeof window !== 'undefined' ? window.location.origin : '';

  useKeyCommand('metaKey', 'k', onOpen);

  const routeKey = pathWithoutVersionFileId(router.asPath);
  React.useEffect(() => {
    onClose();
  }, [onClose, routeKey]);

  React.useEffect(() => {
    if (isOpen) {
      askInputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSubmit = React.useCallback(() => {
    if (askLoading || !askQuestion.trim()) return;
    const question = askQuestion.trim();
    setAskQuestion('');
    void ask(question);
  }, [ask, askLoading, askQuestion]);

  if (
    !searchClient.client ||
    !indexData ||
    !searchClient.aiSearchHttp ||
    memberManagement.loading ||
    !router.isReady
  ) {
    return (
      <>
        <VersionFilePreviewFromQuery />
        <Tooltip label="Ask AI" isDisabled={memberManagement.loading}>
          <IconButton
            aria-label="Ask AI"
            icon={<AIAskTriggerIcon />}
            variant="outline"
            colorScheme="gray"
            isRound
            isDisabled
          />
        </Tooltip>
      </>
    );
  }

  return (
    <>
      <VersionFilePreviewFromQuery />
      <IconButton
        aria-label="Ask AI"
        icon={<AIAskTriggerIcon />}
        variant="outline"
        colorScheme="gray"
        isRound
        onClick={onOpen}
      />

      <AIAskDrawer
        isOpen={isOpen}
        askError={askError}
        askMessages={askMessages}
        askQuestion={askQuestion}
        askLoading={askLoading}
        indexData={indexData}
        appOrigin={appOrigin}
        viewResultsLoadingIndex={viewResultsLoadingIndex}
        inputRef={askInputRef}
        onClose={onClose}
        onQuestionChange={setAskQuestion}
        onSubmit={handleSubmit}
        onNewChat={clearPersistedChat}
        onViewResults={(index) => void handleViewResults(index)}
      />
    </>
  );
};
